/**
 * Partner Scorecard Page - Phase 7
 * Performance intelligence and accountability system
 * ALL calculations from backend - NO duplicated frontend logic
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users,
  TrendUp,
  TrendDown,
  Star,
  Medal,
  Warning,
  Clock,
  Target,
  ChartLine,
  CaretDown,
  CaretUp,
  CaretRight,
  ArrowsClockwise,
  X,
  CheckCircle,
  WarningCircle,
  Lightning,
  ShieldCheck,
  ShieldWarning,
  Scales,
  Info,
  ArrowUp,
  ArrowDown,
} from '@phosphor-icons/react';
import { LoadingPage, EmptyState } from '../components/common';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { partnersApi } from '../utils/api';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { cn } from '../lib/utils';

// Score label configurations
const SCORE_LABELS = {
  top_performer: { label: 'Top Performer', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Medal },
  strong: { label: 'Strong', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: TrendUp },
  average: { label: 'Average', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Target },
  weak: { label: 'Weak', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: TrendDown },
  high_risk: { label: 'High Risk', color: 'bg-red-100 text-red-700 border-red-200', icon: Warning },
};

const RECOMMENDATION_COLORS = {
  scale_up: 'bg-emerald-100 text-emerald-700',
  maintain: 'bg-blue-100 text-blue-700',
  monitor: 'bg-amber-100 text-amber-700',
  reduce_exposure: 'bg-red-100 text-red-700',
};

const ROLE_LABELS = {
  sourcer: 'Deal Sourcer',
  project_manager: 'Project Manager',
  broker: 'Broker',
  architect: 'Architect',
  investor: 'Investor',
};

const RISK_SEVERITY_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

// Overview metric card
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendPositive }) => (
  <Card data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold font-tabular mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {trend !== undefined && (
            <span className={cn(
              "text-xs font-medium",
              trendPositive ? "text-emerald-600" : "text-red-600"
            )}>
              {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
            </span>
          )}
          {Icon && <Icon size={24} className="text-muted-foreground" />}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Score breakdown visualization
const ScoreBreakdown = ({ breakdown }) => {
  if (!breakdown) return null;
  
  const components = [
    { name: 'Deal Quality', score: breakdown.deal_quality_score, weight: breakdown.deal_quality_weight },
    { name: 'Forecast Accuracy', score: breakdown.forecast_accuracy_score, weight: breakdown.forecast_accuracy_weight },
    { name: 'Budget Discipline', score: breakdown.budget_discipline_score, weight: breakdown.budget_discipline_weight },
    { name: 'Timeline Discipline', score: breakdown.timeline_discipline_score, weight: breakdown.timeline_discipline_weight },
    { name: 'Consistency', score: breakdown.consistency_score, weight: breakdown.consistency_weight },
  ];

  return (
    <div className="space-y-3" data-testid="score-breakdown">
      {components.map((comp) => (
        <div key={comp.name}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span>{comp.name} ({comp.weight}%)</span>
            <span className="font-medium">{comp.score.toFixed(0)}/100</span>
          </div>
          <Progress 
            value={comp.score} 
            className="h-2"
          />
        </div>
      ))}
    </div>
  );
};

// Partner detail modal
const PartnerDetailModal = ({ partner, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!partner) return null;
  
  const ScoreLabelConfig = SCORE_LABELS[partner.score_label] || SCORE_LABELS.average;
  const ScoreLabelIcon = ScoreLabelConfig.icon;
  
  return (
    <Dialog open={!!partner} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Users size={24} />
            {partner.partner_name}
            <Badge className={cn("ml-2", ScoreLabelConfig.color)}>
              <ScoreLabelIcon size={14} className="mr-1" />
              {ScoreLabelConfig.label}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="deals">Deals ({partner.total_deals})</TabsTrigger>
            <TabsTrigger value="metrics">Metrics</TabsTrigger>
            <TabsTrigger value="comparison">Comparison</TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-4">
            {/* Score Card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Overall Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="relative w-24 h-24">
                      <svg className="w-24 h-24 transform -rotate-90">
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="none"
                          className="text-secondary"
                        />
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={`${(partner.score / 100) * 251.2} 251.2`}
                          className={cn(
                            partner.score >= 70 ? "text-emerald-500" :
                            partner.score >= 50 ? "text-amber-500" : "text-red-500"
                          )}
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold">
                        {partner.score.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <ScoreBreakdown breakdown={partner.score_breakdown} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Recommendation */}
              {partner.recommendation && (
                <Card className="border-2 border-[#3A4A40]" data-testid="partner-recommendation">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Lightning size={18} className="text-[#3A4A40]" />
                      Recommendation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge className={cn("mb-3 capitalize", RECOMMENDATION_COLORS[partner.recommendation.action])}>
                      {partner.recommendation.action.replace('_', ' ')}
                    </Badge>
                    <p className="text-sm text-muted-foreground">{partner.recommendation.reason}</p>
                    
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      {partner.recommendation.strong_areas?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-emerald-700 mb-1">Strong Areas</p>
                          <ul className="space-y-1">
                            {partner.recommendation.strong_areas.map((area, i) => (
                              <li key={i} className="flex items-center gap-1 text-xs text-muted-foreground">
                                <CheckCircle size={12} className="text-emerald-600" />
                                {area}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {partner.recommendation.weak_areas?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-red-700 mb-1">Weak Areas</p>
                          <ul className="space-y-1">
                            {partner.recommendation.weak_areas.map((area, i) => (
                              <li key={i} className="flex items-center gap-1 text-xs text-muted-foreground">
                                <WarningCircle size={12} className="text-red-600" />
                                {area}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Risk Signals */}
            {partner.risk_signals?.length > 0 && (
              <Card data-testid="risk-signals">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Warning size={18} />
                    Risk Signals ({partner.risk_signals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {partner.risk_signals.map((signal, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30">
                        <Badge className={cn("shrink-0", RISK_SEVERITY_COLORS[signal.severity])}>
                          {signal.severity}
                        </Badge>
                        <div className="flex-1">
                          <p className="text-sm">{signal.message}</p>
                          {signal.metric_value !== null && signal.threshold !== null && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Value: {signal.metric_value.toFixed(1)} | Threshold: {signal.threshold}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Deals Tab */}
          <TabsContent value="deals" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Deal Performance History</CardTitle>
              </CardHeader>
              <CardContent>
                {partner.deal_performances?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/30">
                        <TableHead>Deal</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="text-right">ROI</TableHead>
                        <TableHead className="text-right">Reno Deviation</TableHead>
                        <TableHead className="text-right">Timeline</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partner.deal_performances.map((deal) => (
                        <TableRow key={deal.deal_id}>
                          <TableCell>
                            <Link 
                              to={`/deal/${deal.deal_id}`}
                              className="font-medium text-primary hover:underline"
                            >
                              {deal.deal_name}
                            </Link>
                          </TableCell>
                          <TableCell className="capitalize">{deal.role_in_deal}</TableCell>
                          <TableCell className={cn(
                            "text-right font-tabular",
                            deal.roi_percent >= 15 ? "text-emerald-600" :
                            deal.roi_percent >= 10 ? "text-amber-600" : "text-red-600"
                          )}>
                            {deal.roi_percent?.toFixed(1) || '-'}%
                          </TableCell>
                          <TableCell className={cn(
                            "text-right font-tabular",
                            deal.renovation_deviation_percent === null ? "text-muted-foreground" :
                            deal.renovation_deviation_percent <= 0 ? "text-emerald-600" :
                            deal.renovation_deviation_percent <= 10 ? "text-amber-600" : "text-red-600"
                          )}>
                            {deal.renovation_deviation_percent !== null 
                              ? `${deal.renovation_deviation_percent > 0 ? '+' : ''}${deal.renovation_deviation_percent.toFixed(1)}%`
                              : '-'
                            }
                          </TableCell>
                          <TableCell className={cn(
                            "text-right font-tabular",
                            deal.timeline_deviation_days === null ? "text-muted-foreground" :
                            deal.timeline_deviation_days <= 0 ? "text-emerald-600" :
                            deal.timeline_deviation_days <= 14 ? "text-amber-600" : "text-red-600"
                          )}>
                            {deal.timeline_deviation_days !== null 
                              ? `${deal.timeline_deviation_days > 0 ? '+' : ''}${deal.timeline_deviation_days}d`
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {deal.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <EmptyState
                    icon={Info}
                    title="No Deals"
                    description="Ce partenaire n'a pas encore de deals associés"
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Metrics Tab */}
          <TabsContent value="metrics" className="space-y-6 mt-4">
            {/* Financial Performance */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Financial Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Total Profit</p>
                    <p className="text-lg font-bold font-tabular">{formatCurrency(partner.total_profit_generated)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Avg Profit/Deal</p>
                    <p className="text-lg font-bold font-tabular">{formatCurrency(partner.avg_profit_per_deal)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Avg ROI</p>
                    <p className={cn(
                      "text-lg font-bold",
                      partner.avg_roi_percent >= 15 ? "text-emerald-600" :
                      partner.avg_roi_percent >= 10 ? "text-amber-600" : "text-red-600"
                    )}>{partner.avg_roi_percent?.toFixed(1)}%</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Avg Margin</p>
                    <p className="text-lg font-bold font-tabular">{partner.avg_margin_percent?.toFixed(1)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Forecast Accuracy */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Forecast Accuracy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Overall Accuracy</p>
                    <p className={cn(
                      "text-lg font-bold",
                      partner.overall_forecast_accuracy_percent >= 85 ? "text-emerald-600" :
                      partner.overall_forecast_accuracy_percent >= 70 ? "text-amber-600" : "text-red-600"
                    )}>{partner.overall_forecast_accuracy_percent?.toFixed(1)}%</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Acquisition Deviation</p>
                    <p className="text-lg font-bold font-tabular">
                      {partner.avg_acquisition_deviation_percent !== null 
                        ? `${partner.avg_acquisition_deviation_percent > 0 ? '+' : ''}${partner.avg_acquisition_deviation_percent.toFixed(1)}%`
                        : '-'
                      }
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Renovation Deviation</p>
                    <p className="text-lg font-bold font-tabular">
                      {partner.avg_renovation_deviation_percent !== null 
                        ? `${partner.avg_renovation_deviation_percent > 0 ? '+' : ''}${partner.avg_renovation_deviation_percent.toFixed(1)}%`
                        : '-'
                      }
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Exit Deviation</p>
                    <p className="text-lg font-bold font-tabular">
                      {partner.avg_exit_deviation_percent !== null 
                        ? `${partner.avg_exit_deviation_percent > 0 ? '+' : ''}${partner.avg_exit_deviation_percent.toFixed(1)}%`
                        : '-'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Budget & Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Budget Control</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Avg Overrun</span>
                      <span className={cn(
                        "font-bold font-tabular",
                        partner.avg_renovation_overrun_percent <= 5 ? "text-emerald-600" :
                        partner.avg_renovation_overrun_percent <= 15 ? "text-amber-600" : "text-red-600"
                      )}>
                        {partner.avg_renovation_overrun_percent > 0 ? '+' : ''}{partner.avg_renovation_overrun_percent?.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Budget Adherence</span>
                      <span className="font-bold">{partner.budget_adherence_rate?.toFixed(0)}%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Within Budget: {partner.projects_within_budget}</span>
                      <span>Over Budget: {partner.projects_over_budget}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Timeline Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Avg Delay</span>
                      <span className={cn(
                        "font-bold font-tabular",
                        partner.avg_delay_days <= 0 ? "text-emerald-600" :
                        partner.avg_delay_days <= 14 ? "text-amber-600" : "text-red-600"
                      )}>
                        {partner.avg_delay_days > 0 ? '+' : ''}{partner.avg_delay_days} days
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">On-Time Rate</span>
                      <span className="font-bold">{partner.projects_on_time_percent?.toFixed(0)}%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Worst Delay: {partner.worst_delay_days} days</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Comparison Tab */}
          <TabsContent value="comparison" className="mt-4">
            {partner.portfolio_comparison && (
              <Card data-testid="portfolio-comparison">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Scales size={18} />
                    Portfolio Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-secondary/30 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground mb-1">Rank</p>
                      <p className="text-2xl font-bold">
                        #{partner.portfolio_comparison.rank_among_partners}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        of {partner.portfolio_comparison.total_partners}
                      </p>
                    </div>
                    <div className="p-4 bg-secondary/30 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground mb-1">Percentile</p>
                      <p className="text-2xl font-bold">
                        {partner.portfolio_comparison.percentile_rank}%
                      </p>
                    </div>
                    <div className="p-4 bg-secondary/30 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground mb-1">vs Best</p>
                      <p className={cn(
                        "text-2xl font-bold",
                        partner.portfolio_comparison.vs_best_performer_gap === 0 ? "text-emerald-600" : "text-muted-foreground"
                      )}>
                        {partner.portfolio_comparison.vs_best_performer_gap === 0 ? 'You' : `-${partner.portfolio_comparison.vs_best_performer_gap.toFixed(0)}`}
                      </p>
                    </div>
                    <div className="p-4 bg-secondary/30 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground mb-1">vs Worst</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        +{partner.portfolio_comparison.vs_worst_performer_gap.toFixed(0)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span>Partner Score</span>
                        <span className="font-bold">{partner.portfolio_comparison.partner_score.toFixed(1)}</span>
                      </div>
                      <div className="relative h-4 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className={cn(
                            "absolute h-full rounded-full",
                            partner.portfolio_comparison.better_than_avg ? "bg-emerald-500" : "bg-amber-500"
                          )}
                          style={{ width: `${partner.portfolio_comparison.partner_score}%` }}
                        />
                        <div 
                          className="absolute h-full w-0.5 bg-slate-600"
                          style={{ left: `${partner.portfolio_comparison.portfolio_avg_score}%` }}
                          title={`Portfolio Avg: ${partner.portfolio_comparison.portfolio_avg_score}`}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 text-center">
                        Portfolio Average: {partner.portfolio_comparison.portfolio_avg_score.toFixed(1)}
                        {partner.portfolio_comparison.better_than_avg && (
                          <span className="text-emerald-600 ml-2">Above Average</span>
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Main component
export const PartnerScorecard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const actionFromUrl = searchParams.get('action');
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [sortField, setSortField] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [roleFilter, setRoleFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewData, partnersData] = await Promise.all([
        partnersApi.getScorecardOverview(),
        partnersApi.getAllScorecards()
      ]);
      setOverview(overviewData);
      setPartners(partnersData);
    } catch (err) {
      console.error('Failed to fetch partner scorecard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle action URL param
  useEffect(() => {
    if (actionFromUrl === 'add') {
      // TODO: Open add partner modal when implemented
      // For now, show info message
      import('sonner').then(({ toast }) => {
        toast.info('Partner creation form coming soon. Partners are currently managed through deals.');
      });
      // Clear the action param from URL
      setSearchParams(prev => {
        prev.delete('action');
        return prev;
      });
    }
  }, [actionFromUrl, setSearchParams]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Sort and filter partners
  const sortedPartners = React.useMemo(() => {
    let filtered = [...partners];
    
    // Filter by role
    if (roleFilter !== 'all') {
      filtered = filtered.filter(p => p.role === roleFilter);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [partners, sortField, sortDirection, roleFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />;
  };

  const handleViewPartner = async (partnerId) => {
    try {
      const data = await partnersApi.getScorecard(partnerId);
      setSelectedPartner(data);
    } catch (err) {
      console.error('Failed to fetch partner details:', err);
    }
  };

  if (loading) {
    return <LoadingPage message="Chargement des fiches partenaires..." />;
  }

  return (
    <div className="space-y-6 p-6" data-testid="partner-scorecard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Partner Scorecard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Performance intelligence and accountability
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
        >
          <ArrowsClockwise size={16} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Overview Metrics */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="overview-metrics">
          <MetricCard
            title="Total Partners"
            value={overview.total_partners}
            subtitle={`${overview.active_partners} active`}
            icon={Users}
          />
          <MetricCard
            title="Avg Score"
            value={overview.avg_partner_score.toFixed(1)}
            subtitle="Portfolio average"
            icon={Star}
          />
          <MetricCard
            title="Top Performer"
            value={overview.top_performer?.name || '-'}
            subtitle={overview.top_performer ? `Score: ${overview.top_performer.score.toFixed(0)}` : ''}
            icon={Medal}
          />
          <MetricCard
            title="Portfolio Profit"
            value={formatCurrency(overview.portfolio_total_profit)}
            subtitle={`Avg ROI: ${overview.portfolio_avg_roi.toFixed(1)}%`}
            icon={TrendUp}
          />
          <MetricCard
            title="Top Performers"
            value={overview.partners_by_score_tier?.top_performer || 0}
            subtitle={`Strong: ${overview.partners_by_score_tier?.strong || 0}`}
            icon={CheckCircle}
          />
          <MetricCard
            title="At Risk"
            value={(overview.partners_by_score_tier?.weak || 0) + (overview.partners_by_score_tier?.high_risk || 0)}
            subtitle={`High risk: ${overview.partners_by_score_tier?.high_risk || 0}`}
            icon={Warning}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="sourcer">Deal Sourcers</SelectItem>
            <SelectItem value="project_manager">Project Managers</SelectItem>
            <SelectItem value="broker">Brokers</SelectItem>
            <SelectItem value="architect">Architects</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          Showing {sortedPartners.length} of {partners.length} partners
        </p>
      </div>

      {/* Partners Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30">
                <TableHead 
                  className="cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('partner_name')}
                >
                  <div className="flex items-center gap-1">
                    Partner <SortIcon field="partner_name" />
                  </div>
                </TableHead>
                <TableHead>Role</TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('total_deals')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Deals <SortIcon field="total_deals" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('total_profit_generated')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Total Profit <SortIcon field="total_profit_generated" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('avg_roi_percent')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Avg ROI <SortIcon field="avg_roi_percent" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('overall_forecast_accuracy_percent')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Accuracy <SortIcon field="overall_forecast_accuracy_percent" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('avg_renovation_overrun_percent')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Budget Dev <SortIcon field="avg_renovation_overrun_percent" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('avg_delay_days')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Avg Delay <SortIcon field="avg_delay_days" />
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-secondary/50"
                  onClick={() => handleSort('score')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Score <SortIcon field="score" />
                  </div>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedPartners.length > 0 ? (
                sortedPartners.map((partner) => {
                  const ScoreLabelConfig = SCORE_LABELS[partner.score_label] || SCORE_LABELS.average;
                  const ScoreLabelIcon = ScoreLabelConfig.icon;
                  
                  return (
                    <TableRow 
                      key={partner.partner_id}
                      data-testid={`partner-row-${partner.partner_id}`}
                      className="hover:bg-secondary/10"
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                            <Users size={16} className="text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{partner.partner_name}</p>
                            {partner.company && (
                              <p className="text-xs text-muted-foreground">{partner.company}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {ROLE_LABELS[partner.role] || partner.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-tabular">
                        {partner.total_deals}
                      </TableCell>
                      <TableCell className="text-right font-tabular">
                        {formatCurrency(partner.total_profit_generated)}
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-tabular font-medium",
                        partner.avg_roi_percent >= 15 ? "text-emerald-600" :
                        partner.avg_roi_percent >= 10 ? "text-amber-600" : "text-red-600"
                      )}>
                        {partner.avg_roi_percent?.toFixed(1)}%
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-tabular font-medium",
                        partner.overall_forecast_accuracy_percent >= 85 ? "text-emerald-600" :
                        partner.overall_forecast_accuracy_percent >= 70 ? "text-amber-600" : "text-red-600"
                      )}>
                        {partner.overall_forecast_accuracy_percent?.toFixed(0)}%
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-tabular font-medium",
                        partner.avg_renovation_overrun_percent <= 5 ? "text-emerald-600" :
                        partner.avg_renovation_overrun_percent <= 15 ? "text-amber-600" : "text-red-600"
                      )}>
                        {partner.avg_renovation_overrun_percent > 0 ? '+' : ''}{partner.avg_renovation_overrun_percent?.toFixed(0)}%
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-tabular font-medium",
                        partner.avg_delay_days <= 0 ? "text-emerald-600" :
                        partner.avg_delay_days <= 14 ? "text-amber-600" : "text-red-600"
                      )}>
                        {partner.avg_delay_days > 0 ? '+' : ''}{partner.avg_delay_days}d
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={cn(
                            "text-lg font-bold",
                            partner.score >= 70 ? "text-emerald-600" :
                            partner.score >= 50 ? "text-amber-600" : "text-red-600"
                          )}>
                            {partner.score.toFixed(0)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("border", ScoreLabelConfig.color)}>
                          <ScoreLabelIcon size={12} className="mr-1" />
                          {ScoreLabelConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewPartner(partner.partner_id)}
                          className="gap-1"
                          data-testid={`view-partner-${partner.partner_id}`}
                        >
                          Details
                          <CaretRight size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={11} className="h-32">
                    <EmptyState
                      icon={Users}
                      title="No Partners"
                      description="Aucun partenaire ne correspond au filtre actuel"
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Partner Detail Modal */}
      <PartnerDetailModal
        partner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
      />

      {/* Page Help */}
      <PageHelpBlock 
        pageKey="partner-scorecard" 
        content={pageExplanations.partnerScorecard} 
      />
    </div>
  );
};

export default PartnerScorecard;
